/**
 * TAILWIND PROGRESS BAR KOMPONENTE
 *
 * Schritt-Anzeige für mehrstufige Prozesse (z.B. Buchungsflow).
 * Horizontal auf Desktop, vertikal auf Mobile.
 *
 * Props:
 *   steps       - Array von { id: string, name: string, status: 'complete' | 'current' | 'upcoming' }
 *   onStepClick? - (stepIndex: number) => void, nur für abgeschlossene Schritte
 *   variant?    - gold (default, Brand-Farbe) | teal
 *
 * Usage:
 *   <TWProgressBar
 *     steps={[
 *       { id: '1', name: 'Termin', status: 'complete' },
 *       { id: '2', name: 'Zahlung', status: 'current' },
 *       { id: '3', name: 'Bestätigung', status: 'upcoming' },
 *     ]}
 *     variant="gold"
 *   />
 */
import React from 'react';

export interface ProgressStep {
  id: string;
  name: string;
  status: 'complete' | 'current' | 'upcoming';
}

interface TWProgressBarProps {
  steps: ProgressStep[];
  onStepClick?: (stepIndex: number) => void;
  variant?: 'gold' | 'teal';
}

export const TWProgressBar: React.FC<TWProgressBarProps> = ({
  steps,
  onStepClick,
  variant = 'gold'
}) => {
  const accentClasses = {
    gold: {
      complete: 'border-brand',
      current: 'border-brand',
      upcoming: 'border-white/10',
      completeText: 'text-brand group-hover:text-brand-hover',
      currentText: 'text-brand',
      upcomingText: 'text-gray-500 group-hover:text-gray-400'
    },
    teal: {
      complete: 'border-teal-500',
      current: 'border-teal-500',
      upcoming: 'border-white/10',
      completeText: 'text-teal-400 group-hover:text-teal-300',
      currentText: 'text-teal-400',
      upcomingText: 'text-gray-500 group-hover:text-gray-400'
    }
  };

  const colors = accentClasses[variant];

  const handleStepClick = (stepIndex: number, status: string) => {
    if (status === 'complete' && onStepClick) {
      onStepClick(stepIndex);
    }
  };

  return (
    <nav aria-label="Progress">
      <ol role="list" className="space-y-4 md:flex md:space-y-0 md:space-x-8">
        {steps.map((step, index) => (
          <li key={step.id} className="md:flex-1">
            {step.status === 'complete' ? (
              <button
                type="button"
                onClick={() => handleStepClick(index, step.status)}
                className={`group flex w-full flex-col border-l-4 ${colors.complete} py-2 pl-4 hover:border-opacity-70 md:border-t-4 md:border-l-0 md:pt-4 md:pb-0 md:pl-0 transition-colors`}
              >
                <span className={`text-sm font-medium ${colors.completeText} transition-colors`}>
                  {step.id}
                </span>
                <span className="text-sm font-medium text-gray-300">
                  {step.name}
                </span>
              </button>
            ) : step.status === 'current' ? (
              <div
                aria-current="step"
                className={`flex flex-col border-l-4 ${colors.current} py-2 pl-4 md:border-t-4 md:border-l-0 md:pt-4 md:pb-0 md:pl-0`}
              >
                <span className={`text-sm font-medium ${colors.currentText}`}>
                  {step.id}
                </span>
                <span className="text-sm font-medium text-gray-300">
                  {step.name}
                </span>
              </div>
            ) : (
              <div
                className={`group flex flex-col border-l-4 ${colors.upcoming} py-2 pl-4 md:border-t-4 md:border-l-0 md:pt-4 md:pb-0 md:pl-0`}
              >
                <span className={`text-sm font-medium ${colors.upcomingText} transition-colors`}>
                  {step.id}
                </span>
                <span className="text-sm font-medium text-gray-400">
                  {step.name}
                </span>
              </div>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default TWProgressBar;
